import { MouseEvent, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Actions,
  CancelButton,
  ConfirmButton,
  Dialog,
  Message,
  Overlay,
  Title,
} from "./styles";
import { ConfirmationModalProps } from "./types";

export function ConfirmationModal({
  isOpen,
  title,
  children,
  cancelLabel = "Cancelar",
  confirmLabel,
  loadingLabel = "Processando...",
  isLoading = false,
  onCancel,
  onConfirm,
}: ConfirmationModalProps) {
  const titleId = useId();
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cancelButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isLoading) onCancel();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isLoading, isOpen, onCancel]);

  if (!isOpen) return null;

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && !isLoading) onCancel();
  };

  return createPortal(
    <Overlay onMouseDown={handleOverlayClick}>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <Title id={titleId}>{title}</Title>
        <Message>{children}</Message>
        <Actions>
          <CancelButton
            ref={cancelButtonRef}
            type="button"
            disabled={isLoading}
            onClick={onCancel}
          >
            {cancelLabel}
          </CancelButton>
          <ConfirmButton type="button" disabled={isLoading} onClick={onConfirm}>
            {isLoading ? loadingLabel : confirmLabel}
          </ConfirmButton>
        </Actions>
      </Dialog>
    </Overlay>,
    document.body,
  );
}

export type { ConfirmationModalProps } from "./types";
